import { getItem, setItem, removeItem } from "@lib/storage";
import { isAfter, subDays } from "date-fns";
import Share from "react-native-share";
import DeviceInfo from "react-native-device-info";
import InAppUpdates, { IAUUpdateKind } from "sp-react-native-in-app-updates";
import analytics from "@services/analytics";
import { shouldInAppUpdate } from "@services/api";

const InAppUpdatesInstance = new InAppUpdates();

const isDeviceSupported = async () => {
  try {
    const isEmulator = await DeviceInfo.isEmulator();
    if (isEmulator) return false;
    const { isInstalled: isPSInstalled } = await Share.isPackageInstalled(
      "com.android.vending",
    );
    if (!isPSInstalled) return false;
    return true;
  } catch (e) {
    console.warn(e);
    return false;
  }
};

const isUpdateAvailable = async (type) => {
  try {
    const inAppUpdateOptions = {
      curVersion: DeviceInfo.getVersion(),
    };
    const result =
      await InAppUpdatesInstance.checkNeedsUpdate(inAppUpdateOptions);

    if (!result) return "NO_UPDATE";

    if (result.shouldUpdate) {
      if (type === "IMMEDIATE") {
        if (result.other.isImmediateUpdateAllowed) {
          return "IMMEDIATE";
        } else if (result.other.isFlexibleUpdateAllowed) {
          analytics.trackEvent("FLEXIBLE_UPDATE_SHOWN", {
            curVersion: DeviceInfo.getVersion(),
          });
          return "FLEXIBLE";
        }
      } else if (type === "FLEXIBLE") {
        if (result.other.isFlexibleUpdateAllowed) {
          return "FLEXIBLE";
        }
      }
    } else {
      return "NO_UPDATE";
    }
  } catch (e) {
    console.warn(e);
    return "NO_UPDATE";
  }
};

const invokeInAppUpdate = async () => {
  try {
    const supported = await isDeviceSupported();
    if (!supported) {
      return;
    }
    const { data } = await shouldInAppUpdate(DeviceInfo.getVersion());
    if (!data || !data.show_update_modal) {
      return;
    }
    const { nudge_interval_days, update_type } = data || {};
    const updateAvailabletype = await isUpdateAvailable(update_type);
    if (updateAvailabletype === "NO_UPDATE") {
      return;
    }

    if (updateAvailabletype === "IMMEDIATE") {
      downloadUpdate(IAUUpdateKind.IMMEDIATE);
    } else if (updateAvailabletype === "FLEXIBLE") {
      if (!nudge_interval_days) {
        return;
      }
      const lastCheckedForAppUpdate = await getItem(
        "lastCheckedForAppUpdate",
      );
      const nudgeIntervalForAppUpdate = await getItem(
        "nudgeIntervalForAppUpdate",
      );

      if (!lastCheckedForAppUpdate) {
        downloadUpdate(IAUUpdateKind.FLEXIBLE);
        await setItem(
          "lastCheckedForAppUpdate",
          `${new Date().getTime()}`,
        );
        await removeItem("nudgeIntervalForAppUpdate");
        await setItem(
          "nudgeIntervalForAppUpdate",
          `${nudge_interval_days}`,
        );
      } else {
        if (
          isAfter(
            subDays(new Date(), nudge_interval_days),
            new Date(parseInt(lastCheckedForAppUpdate, 10)),
          ) ||
          `${nudge_interval_days}` !== `${nudgeIntervalForAppUpdate}`
        ) {
          downloadUpdate(IAUUpdateKind.FLEXIBLE);
          await setItem(
            "lastCheckedForAppUpdate",
            `${new Date().getTime()}`,
          );
          await removeItem("nudgeIntervalForAppUpdate");
          await setItem(
            "nudgeIntervalForAppUpdate",
            `${nudge_interval_days}`,
          );
        }
      }
    }
  } catch (e) {
    console.warn(e);
  }
};

const downloadUpdate = async (updateType) => {
  InAppUpdatesInstance.startUpdate({
    updateType,
  });
};

const inAppUpdateListener = (callBack) => {
  const callback = (data) => {
    const { status, bytesDownloaded, totalBytesToDownload } = data || {};
    console.log(`
       Status ${status},
       Downloaded ${bytesDownloaded},
       Total Size ${totalBytesToDownload}
     `);

    if (typeof callBack === "function") {
      callBack(status, bytesDownloaded, totalBytesToDownload);
    }

    if (status === 11) {
      InAppUpdatesInstance.installUpdate();
    }
  };

  InAppUpdatesInstance.addStatusUpdateListener(callback);
  return () => {
    InAppUpdatesInstance.removeStatusUpdateListener(callback);
  };
};

export { inAppUpdateListener, invokeInAppUpdate };
